import React from 'react';
import { Check, Loader2, ClipboardCheck, Users, FileSearch, Sparkles } from 'lucide-react';
import type { EchelonProgressStage } from '../controllers/FormController';

type Step = {
  stage: EchelonProgressStage;
  label: string;
  description: string;
  Icon: React.ComponentType<{ className?: string }>;
};

const STEPS: Step[] = [
  {
    stage: 'starting',
    label: 'Verifying your eligibility',
    description: 'Checking your details against our criteria',
    Icon: ClipboardCheck,
  },
  {
    stage: 'processing',
    label: 'Connecting with solution specialists',
    description: 'Your application is being assigned to our team',
    Icon: Users,
  },
  {
    stage: 'reviewing',
    label: 'Reviewing your application',
    description: 'Analysing your profile and available options',
    Icon: FileSearch,
  },
  {
    stage: 'finalizing',
    label: 'Preparing your personalised options',
    description: 'Almost there — compiling your results',
    Icon: Sparkles,
  },
];

const STAGE_ORDER: EchelonProgressStage[] = ['starting', 'processing', 'reviewing', 'finalizing'];

type EchelonProgressOverlayProps = {
  activeStage: EchelonProgressStage;
};

export default function EchelonProgressOverlay({ activeStage }: EchelonProgressOverlayProps) {
  const activeIndex = STAGE_ORDER.indexOf(activeStage);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 flex flex-col gap-6 max-w-md w-full mx-4">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900">Processing your application</h3>
          <p className="text-sm text-gray-500 mt-1">This may take 1–2 minutes. Please keep this page open.</p>
        </div>

        <div className="space-y-3">
          {STEPS.map((step, index) => {
            const isCompleted = index < activeIndex;
            const isActive = index === activeIndex;
            const isPending = index > activeIndex;
            const { Icon } = step;

            return (
              <div
                key={step.stage}
                className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                  isActive ? 'bg-[#212d52]/10' : 'bg-gray-50'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isCompleted
                      ? 'bg-green-500 text-white'
                      : isActive
                        ? 'bg-[#212d52] text-white'
                        : 'bg-gray-200 text-gray-400'
                  }`}
                >
                  {isCompleted ? (
                    <Check className="w-4 h-4" />
                  ) : isActive ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className={`font-medium text-sm ${isPending ? 'text-gray-400' : 'text-gray-900'}`}>
                    {step.label}
                  </p>
                  <p className={`text-xs mt-0.5 ${isPending ? 'text-gray-300' : 'text-gray-500'}`}>
                    {step.description}
                  </p>
                </div>

                {isCompleted && (
                  <span className="text-green-500 text-xs font-medium flex-shrink-0 mt-0.5">Done</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
